import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { StipUploadModal } from '../components/StipUploadModal';
import { getMissingStips } from '../lib/api';
import { toast } from 'sonner';
import { Upload, Mail, FileText, AlertTriangle, CheckCircle, Clock, Search, X } from 'lucide-react';
import { Input } from '../components/ui/input';

export const StipTrackerPage = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploadModal, setUploadModal] = useState({ open: false, dealId: '', stipName: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await getMissingStips();
      setDeals(res.data);
    } catch (err) {
      toast.error('Failed to load missing stips');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadStip = (dealId, stipName) => {
    setUploadModal({ open: true, dealId, stipName });
  };

  const handleUploadSuccess = () => {
    loadData();
  };

  const handleRequestAll = (deal) => {
    // In production, this would trigger emails/SMS to customer
    toast.success(`Document request sent to ${deal.customer_name}`);
  };

  const filteredDeals = deals.filter(d => 
    d.deal.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    d.deal.deal_number.includes(search) ||
    d.deal.lender.toLowerCase().includes(search.toLowerCase())
  );

  const totalMissing = deals.reduce((acc, d) => acc + d.missing_stips.length, 0);

  return (
    <Layout>
      <Header
        title="Missing Stip Tracker"
        subtitle={`${totalMissing} documents pending across ${deals.length} deals`}
        onRefresh={loadData}
      />

      <div className="p-6 space-y-6" data-testid="stip-tracker">
        {/* Stats Bar */}
        <div className="bg-white border border-slate-200 p-4 flex items-center gap-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 flex items-center justify-center">
              <AlertTriangle size={24} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-slate-900">{totalMissing}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Missing</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 flex items-center justify-center">
              <Clock size={24} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-mono font-bold text-slate-900">{deals.length}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Deals Pending</p>
            </div>
          </div>

          <div className="flex-1" />

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search deals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-64"
              data-testid="search-stips"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Deals List */}
        {loading ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <p className="text-slate-500">Loading...</p>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
            <p className="text-slate-700 font-medium">All documents collected!</p>
            <p className="text-slate-500 text-sm">No pending stipulations</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDeals.map((item) => (
              <div key={item.deal.id} className="bg-white border border-slate-200" data-testid={`stip-deal-${item.deal.id}`}>
                {/* Deal Header */}
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center font-mono font-bold ${
                      item.deal.deal_age >= 5 ? 'bg-red-100 text-red-700' :
                      item.deal.deal_age >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {item.deal.deal_age}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.deal.customer_name}</p>
                      <p className="text-xs text-slate-500">
                        #{item.deal.deal_number} • {item.deal.lender} • {item.deal.finance_manager}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold">
                      {item.missing_stips.length} Missing
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRequestAll(item.deal)}
                      data-testid={`request-all-${item.deal.id}`}
                    >
                      <Mail size={14} className="mr-1" />
                      Request All
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/deal/${item.deal.id}`)}
                    >
                      Open Deal
                    </Button>
                  </div>
                </div>

                {/* Missing Stips */}
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {item.missing_stips.map((stip, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-red-50 border border-red-200"
                      >
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-red-600" />
                          <span className="text-sm font-medium text-red-700">{stip.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUploadStip(item.deal.id, stip.name)}
                          className="text-xs"
                        >
                          <Upload size={14} className="mr-1" />
                          Upload
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stip Upload Modal */}
      <StipUploadModal
        open={uploadModal.open}
        onClose={() => setUploadModal({ open: false, dealId: '', stipName: '' })}
        stipName={uploadModal.stipName}
        dealId={uploadModal.dealId}
        onSuccess={handleUploadSuccess}
      />
    </Layout>
  );
};
